import React from 'react'
import {
  Box,
  Chip,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import LaunchIcon from '@mui/icons-material/Launch'
import WarningIcon from '@mui/icons-material/Warning'

import { handleLightspeedLinkClick } from '../../../../../utils/externalLinks'
import { renderProductStatus } from '../../../../../config/exceptions/shared/productRenderers'
import { fmtInt, fmtMoney } from '../../../../../config/tools/productCatalogueReview/formatters'
import { NUMERIC_FONT_SX } from '../../../../../theme/numericFont'
import type {
  VariantRow,
  VariantUtilisationRow,
} from '../../../../../config/tools/productCatalogueReview/variantUtilisationConfig'

const renderVariantNameLink = (variant: VariantRow): React.ReactNode => {
  const label = variant.variant_name || variant.name
  if (!variant.lightspeed_variant_link) return label
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Link
        href={variant.lightspeed_variant_link}
        onClick={(e) =>
          handleLightspeedLinkClick(
            e,
            variant.lightspeed_variant_link!,
            `Product ${variant.sku || label}`,
          )
        }
        sx={{
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
          fontWeight: 500,
        }}
      >
        {label}
      </Link>
      <LaunchIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 14 }} />
    </Stack>
  )
}

interface Props {
  family: VariantUtilisationRow
}

const FamilyVariantsTable: React.FC<Props> = ({ family }) => {
  if (!family.variants.length) {
    return (
      <Box sx={{ px: 3, py: 2, backgroundColor: '#FAFAFA' }}>
        <Typography variant="body2" color="text.secondary">
          No variants returned for this family.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ px: 3, py: 2, backgroundColor: '#FAFAFA' }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          display: 'block',
          mb: 1,
        }}
      >
        Variants ({family.variants.length})
      </Typography>
      <Table
        size="small"
        sx={{
          backgroundColor: 'white',
          border: '1px solid #E6E8EA',
          borderRadius: 1,
        }}
      >
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.8125rem' } }}>
            <TableCell sx={{ width: 160 }}>SKU</TableCell>
            <TableCell>Variant name</TableCell>
            <TableCell sx={{ width: 120 }}>Status</TableCell>
            <TableCell align="right" sx={{ width: 140 }}>
              Units sold
            </TableCell>
            <TableCell align="right" sx={{ width: 160 }}>
              Revenue
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {family.variants.map((variant) => {
            const isMismatch =
              !family.parent_of_record_active && variant.active && !variant.is_parent_of_record
            return (
              <TableRow
                key={variant.ls_id}
                sx={{
                  backgroundColor: isMismatch
                    ? 'rgba(237, 108, 2, 0.12)'
                    : undefined,
                }}
              >
                <TableCell
                  sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}
                >
                  {variant.sku || '-'}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {renderVariantNameLink(variant)}
                    {variant.is_parent_of_record ? (
                      <Chip
                        size="small"
                        label="Primary"
                        sx={{
                          height: 18,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          backgroundColor: '#007f98',
                          color: 'white',
                        }}
                      />
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {renderProductStatus(variant.active)}
                    {isMismatch ? (
                      <WarningIcon sx={{ fontSize: 16, color: '#ed6c02' }} />
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell align="right" sx={{ ...NUMERIC_FONT_SX }}>
                  {fmtInt(variant.units_sold)}
                </TableCell>
                <TableCell align="right" sx={{ ...NUMERIC_FONT_SX }}>
                  {fmtMoney(variant.revenue)}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

export default FamilyVariantsTable
